import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Play, CheckCircle2, XCircle, Award, Eye, Loader2, Link2, Copy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { trainingTestsApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const EDITOR_ROLES = ['super_admin', 'admin', 'amministratore', 'rspp', 'docente', 'docente_online', 'formatore'];
const emptyQuestion = () => ({ q: '', options: ['', '', '', ''], correct: 0, is_multi: false });
const emptyForm = () => ({
  title: '',
  course_type: '',
  duration_minutes: 30,
  passing_score_pct: 70,
  questions: [emptyQuestion()],
  recipient_emails: '',  // string CSV nella UI
  pdf_title: '',
  pdf_logo_url: '',
  pdf_footer: '',
});

export default function TrainingTestsPage() {
  const { user } = useAuth();
  const canEdit = EDITOR_ROLES.includes(user?.role);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);

  // Edit dialog
  const [editDialog, setEditDialog] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Play dialog
  const [playDialog, setPlayDialog] = useState(false);
  const [playTest, setPlayTest] = useState(null);
  const [playAnswers, setPlayAnswers] = useState([]);
  const [candidateName, setCandidateName] = useState('');
  const [submitResult, setSubmitResult] = useState(null);
  const [submittingTest, setSubmittingTest] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await trainingTestsApi.list();
      setTests(res.data);
    } catch (e) {
      toast.error('Errore caricamento test');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // === Edit ===
  const openCreate = () => {
    setForm(emptyForm());
    setEditingId(null);
    setEditDialog(true);
  };

  const openEdit = async (test) => {
    try {
      const full = await trainingTestsApi.get(test.id);
      const fd = full.data;
      setForm({
        title: fd.title || '',
        course_type: fd.course_type || '',
        duration_minutes: fd.duration_minutes || 30,
        passing_score_pct: fd.passing_score_pct || 70,
        questions: (fd.questions || []).map((q) => {
          const isMulti = Array.isArray(q.correct);
          return {
            q: q.q || '',
            options: q.options && q.options.length ? q.options : ['', '', '', ''],
            correct: isMulti ? q.correct : (q.correct ?? 0),
            is_multi: isMulti,
          };
        }),
        recipient_emails: (fd.recipient_emails || []).join(', '),
        pdf_title: fd.pdf_title || '',
        pdf_logo_url: fd.pdf_logo_url || '',
        pdf_footer: fd.pdf_footer || '',
      });
      setEditingId(test.id);
      setEditDialog(true);
    } catch (e) {
      toast.error('Errore caricamento test');
    }
  };

  const updateQuestion = (idx, patch) => {
    setForm((prev) => {
      const qs = [...prev.questions];
      qs[idx] = { ...qs[idx], ...patch };
      return { ...prev, questions: qs };
    });
  };

  const updateOption = (qIdx, oIdx, value) => {
    setForm((prev) => {
      const qs = [...prev.questions];
      const opts = [...qs[qIdx].options];
      opts[oIdx] = value;
      qs[qIdx] = { ...qs[qIdx], options: opts };
      return { ...prev, questions: qs };
    });
  };

  const addQuestion = () => {
    setForm((prev) => ({ ...prev, questions: [...prev.questions, emptyQuestion()] }));
  };
  const removeQuestion = (idx) => {
    setForm((prev) => ({ ...prev, questions: prev.questions.filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Inserisci un titolo');
      return;
    }
    const cleanQs = form.questions
      .map((q) => {
        const opts = q.options.map((o) => o.trim()).filter((o) => o);
        if (q.is_multi) {
          // correct = array di indici (rimuovi out-of-bounds)
          const corr = Array.isArray(q.correct) ? q.correct : [q.correct];
          const validCorr = corr.filter((i) => typeof i === 'number' && i >= 0 && i < opts.length);
          return { q: q.q.trim(), options: opts, correct: validCorr.length > 0 ? validCorr : [0] };
        }
        const corrInt = typeof q.correct === 'number' ? q.correct : 0;
        return {
          q: q.q.trim(),
          options: opts,
          correct: corrInt < opts.length ? corrInt : 0,
        };
      })
      .filter((q) => q.q && q.options.length >= 2);
    if (cleanQs.length === 0) {
      toast.error('Aggiungi almeno una domanda con minimo 2 risposte');
      return;
    }
    try {
      setSaving(true);
      const emails = (form.recipient_emails || '')
        .split(/[,;]+/)
        .map((s) => s.trim())
        .filter((s) => s.includes('@'));
      const payload = {
        title: form.title,
        course_type: form.course_type,
        duration_minutes: form.duration_minutes,
        passing_score_pct: form.passing_score_pct,
        questions: cleanQs,
        recipient_emails: emails,
        pdf_title: form.pdf_title || null,
        pdf_logo_url: form.pdf_logo_url || null,
        pdf_footer: form.pdf_footer || null,
      };
      if (editingId) {
        await trainingTestsApi.update(editingId, payload);
        toast.success('Test aggiornato');
      } else {
        await trainingTestsApi.create(payload);
        toast.success('Test creato');
      }
      setEditDialog(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo test?')) return;
    try {
      await trainingTestsApi.delete(id);
      toast.success('Test eliminato');
      load();
    } catch (e) {
      toast.error('Errore');
    }
  };

  const handleDuplicate = async (test) => {
    if (!window.confirm(`Duplicare il test "${test.title}"?`)) return;
    try {
      await trainingTestsApi.duplicate(test.id);
      toast.success('Test duplicato');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore duplicazione');
    }
  };

  // === Genera link pubblico ===
  const handleGeneratePublicLink = async (test) => {
    try {
      const r = await trainingTestsApi.generatePublicLink(test.id);
      const url = `${window.location.origin}/test/${r.data.public_token}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Link copiato negli appunti! Incollalo in WhatsApp/Email.', { duration: 6000 });
      } catch (_) {
        window.prompt('Copia il link qui sotto e invialo ai discenti:', url);
      }
      // eslint-disable-next-line no-console
      console.log('[TrainingTests] public link:', url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore generazione link');
    }
  };

  // === Play ===
  const openPlay = async (test) => {
    try {
      const res = await trainingTestsApi.get(test.id);
      const data = res.data;
      // eslint-disable-next-line no-console
      console.log('[TrainingTests] openPlay', { id: test.id, qcount: (data?.questions || []).length, data });
      const qs = Array.isArray(data?.questions) ? data.questions : [];
      if (qs.length === 0) {
        toast.error("Questo test non contiene domande. Modifica il test e aggiungi almeno una domanda prima di avviarlo.");
        return;
      }
      setPlayTest(data);
      setPlayAnswers(Array(qs.length).fill(-1));
      setCandidateName(user?.full_name || '');
      setSubmitResult(null);
      setPlayDialog(true);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[TrainingTests] openPlay error', e);
      toast.error(e.response?.data?.detail || 'Errore apertura test');
    }
  };

  const submitTest = async () => {
    if (!candidateName.trim()) {
      toast.error('Inserisci il nome del candidato');
      return;
    }
    if (playAnswers.some((a) => a === -1)) {
      if (!window.confirm('Alcune domande sono senza risposta. Inviare comunque?')) return;
    }
    try {
      setSubmittingTest(true);
      const res = await trainingTestsApi.submit(playTest.id, {
        test_id: playTest.id,
        candidate_name: candidateName,
        candidate_email: user?.email,
        answers: playAnswers,
      });
      setSubmitResult(res.data);
    } catch (e) {
      toast.error('Errore invio');
    } finally {
      setSubmittingTest(false);
    }
  };

  return (
    <div className="space-y-6 p-6" data-testid="training-tests-page">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold">Test di Formazione</h1>
          <p className="text-gray-600 mt-1">
            Crea e somministra test di verifica per i discenti. Punteggio minimo per superare: vedi singolo test.
          </p>
        </div>
        {canEdit && (
          <Button onClick={openCreate} data-testid="new-test-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuovo Test
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        </div>
      ) : tests.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            Nessun test ancora. {canEdit && 'Crea il tuo primo test cliccando "Nuovo Test".'}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tests.map((t) => (
            <Card key={t.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-base flex items-start gap-2">
                  <Award className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                  <span>{t.title}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {t.course_type && (
                  <Badge variant="outline" className="mb-2">
                    {t.course_type}
                  </Badge>
                )}
                <div className="text-sm text-gray-600 space-y-1">
                  <div>📝 {t.question_count || 0} domande</div>
                  <div>⏱️ {t.duration_minutes || 30} min</div>
                  <div>🎯 Punteggio minimo: {t.passing_score_pct || 70}%</div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" onClick={() => openPlay(t)} className="flex-1" data-testid={`play-test-${t.id}`}>
                    <Play className="w-4 h-4 mr-1" /> Avvia
                  </Button>
                  {canEdit && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => handleGeneratePublicLink(t)} data-testid={`public-link-${t.id}`} title="Genera link pubblico per somministrazione esterna">
                        <Link2 className="w-4 h-4 text-emerald-600" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDuplicate(t)} data-testid={`duplicate-test-${t.id}`} title="Duplica test">
                        <Copy className="w-4 h-4 text-blue-600" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openEdit(t)} data-testid={`edit-test-${t.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDelete(t.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* === Edit Dialog === */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="edit-test-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Modifica Test' : 'Nuovo Test'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Titolo *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="es. Test Sicurezza Generale 16 ore"
                  data-testid="test-title-input"
                />
              </div>
              <div>
                <Label>Tipo corso</Label>
                <Input
                  value={form.course_type}
                  onChange={(e) => setForm({ ...form, course_type: e.target.value })}
                  placeholder="es. Formazione Specifica Alto Rischio"
                />
              </div>
              <div>
                <Label>Durata (minuti)</Label>
                <Input
                  type="number"
                  value={form.duration_minutes}
                  onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 30 })}
                />
              </div>
              <div>
                <Label>Punteggio minimo (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={form.passing_score_pct}
                  onChange={(e) => setForm({ ...form, passing_score_pct: parseInt(e.target.value) || 70 })}
                />
              </div>
            </div>

            <div className="border-t pt-3">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold">Domande ({form.questions.length})</h3>
                <Button size="sm" variant="outline" onClick={addQuestion}>
                  <Plus className="w-4 h-4 mr-1" /> Aggiungi domanda
                </Button>
              </div>

              <div className="space-y-3">
                {form.questions.map((q, qi) => (
                  <Card key={qi} className="bg-gray-50">
                    <CardContent className="pt-4 space-y-2">
                      <div className="flex gap-2 items-start">
                        <span className="font-bold text-sm pt-2">Q{qi + 1}</span>
                        <Textarea
                          value={q.q}
                          onChange={(e) => updateQuestion(qi, { q: e.target.value })}
                          placeholder="Testo della domanda..."
                          className="flex-1 min-h-16"
                          rows={2}
                        />
                        <Button size="sm" variant="ghost" onClick={() => removeQuestion(qi)} disabled={form.questions.length <= 1}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      <div className="pl-8 flex items-center gap-2">
                        <label className="flex items-center gap-1 text-xs cursor-pointer" data-testid={`q${qi}-multi-toggle`}>
                          <input
                            type="checkbox"
                            checked={!!q.is_multi}
                            onChange={(e) => {
                              const isMulti = e.target.checked;
                              setForm((prev) => {
                                const qs = [...prev.questions];
                                const cur = qs[qi];
                                qs[qi] = {
                                  ...cur,
                                  is_multi: isMulti,
                                  correct: isMulti
                                    ? (Array.isArray(cur.correct) ? cur.correct : [cur.correct])
                                    : (Array.isArray(cur.correct) ? (cur.correct[0] ?? 0) : cur.correct),
                                };
                                return { ...prev, questions: qs };
                              });
                            }}
                          />
                          Risposta multipla
                        </label>
                        {q.is_multi && (
                          <span className="text-[10px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                            Spunta TUTTE le opzioni corrette
                          </span>
                        )}
                      </div>
                      <div className="pl-8 space-y-1">
                        {q.options.map((o, oi) => {
                          const corrArr = Array.isArray(q.correct) ? q.correct : [q.correct];
                          const isOptCorrect = q.is_multi ? corrArr.includes(oi) : q.correct === oi;
                          return (
                          <div key={oi} className="flex items-center gap-2">
                            <input
                              type={q.is_multi ? 'checkbox' : 'radio'}
                              name={`correct-${qi}`}
                              checked={isOptCorrect}
                              onChange={() => {
                                if (q.is_multi) {
                                  setForm((prev) => {
                                    const qs = [...prev.questions];
                                    const cur = Array.isArray(qs[qi].correct) ? [...qs[qi].correct] : [qs[qi].correct];
                                    const idx = cur.indexOf(oi);
                                    if (idx === -1) cur.push(oi); else cur.splice(idx, 1);
                                    cur.sort((a, b) => a - b);
                                    qs[qi] = { ...qs[qi], correct: cur };
                                    return { ...prev, questions: qs };
                                  });
                                } else {
                                  updateQuestion(qi, { correct: oi });
                                }
                              }}
                              title="Risposta corretta"
                            />
                            <span className="font-bold text-sm w-5">{String.fromCharCode(65 + oi)}</span>
                            <Input
                              value={o}
                              onChange={(e) => updateOption(qi, oi, e.target.value)}
                              placeholder={`Risposta ${String.fromCharCode(65 + oi)}`}
                              className="flex-1"
                            />
                            {q.options.length > 2 && (
                              <Button size="sm" variant="ghost" onClick={() => {
                                setForm((prev) => {
                                  const qs = [...prev.questions];
                                  const opts = qs[qi].options.filter((_, idx) => idx !== oi);
                                  let cor = qs[qi].correct;
                                  if (Array.isArray(cor)) {
                                    cor = cor.filter((i) => i !== oi).map((i) => i > oi ? i - 1 : i);
                                  } else if (cor >= opts.length) {
                                    cor = 0;
                                  }
                                  qs[qi] = { ...qs[qi], options: opts, correct: cor };
                                  return { ...prev, questions: qs };
                                });
                              }}>
                                <XCircle className="w-4 h-4 text-gray-400" />
                              </Button>
                            )}
                          </div>
                          );
                        })}
                        {q.options.length < 6 && (
                          <Button size="sm" variant="ghost" onClick={() => {
                            setForm((prev) => {
                              const qs = [...prev.questions];
                              qs[qi] = { ...qs[qi], options: [...qs[qi].options, ''] };
                              return { ...prev, questions: qs };
                            });
                          }}>
                            <Plus className="w-3 h-3 mr-1" /> Aggiungi risposta
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 pl-8">
                        Seleziona il pallino sulla risposta corretta.
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* === Email destinatari + PDF customization === */}
            <div className="space-y-3 p-3 rounded-lg border bg-blue-50/40">
              <div className="font-semibold text-sm">📧 Email risultato test (PDF auto-inviato al submit pubblico)</div>
              <div>
                <Label className="text-xs">Email destinatari aggiuntivi (separate da virgola)</Label>
                <Input
                  value={form.recipient_emails}
                  onChange={(e) => setForm((prev) => ({ ...prev, recipient_emails: e.target.value }))}
                  placeholder="rspp@azienda.it, responsabile@azienda.it"
                  data-testid="recipient-emails-input"
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  Il PDF risultato sarà inviato a queste email + alla tua email (creatore del test).
                  Il mittente sarà la tua email registrata (se il dominio è configurato), altrimenti
                  l'email di sistema con reply-to alla tua email.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Titolo PDF (override)</Label>
                  <Input
                    value={form.pdf_title}
                    onChange={(e) => setForm((prev) => ({ ...prev, pdf_title: e.target.value }))}
                    placeholder="Lascia vuoto per usare il titolo del test"
                    data-testid="pdf-title-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Logo PDF (URL)</Label>
                  <Input
                    value={form.pdf_logo_url}
                    onChange={(e) => setForm((prev) => ({ ...prev, pdf_logo_url: e.target.value }))}
                    placeholder="https://.../logo.png"
                    data-testid="pdf-logo-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Footer PDF</Label>
                <Input
                  value={form.pdf_footer}
                  onChange={(e) => setForm((prev) => ({ ...prev, pdf_footer: e.target.value }))}
                  placeholder="Es. Ing. Mario Rossi – RSPP"
                  data-testid="pdf-footer-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditDialog(false)}>Annulla</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-test-btn">
              {saving ? 'Salvataggio...' : 'Salva Test'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === Play Dialog === */}
      <Dialog open={playDialog} onOpenChange={(o) => { setPlayDialog(o); if (!o) setSubmitResult(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="play-test-dialog">
          <DialogHeader>
            <DialogTitle>{playTest?.title}</DialogTitle>
          </DialogHeader>
          {!submitResult ? (
            <div className="space-y-4">
              <div>
                <Label>Nome candidato *</Label>
                <Input
                  value={candidateName}
                  onChange={(e) => setCandidateName(e.target.value)}
                  placeholder="Cognome e nome"
                  data-testid="candidate-name-input"
                />
              </div>
              <p className="text-sm text-gray-600">
                {playTest?.questions?.length} domande. Punteggio minimo: {playTest?.passing_score_pct || 70}%
              </p>
              {(playTest?.questions || []).map((q, qi) => (
                <Card key={qi}>
                  <CardContent className="pt-4">
                    <p className="font-semibold mb-2">Q{qi + 1}. {q.q}</p>
                    <div className="space-y-1">
                      {(q.options || []).map((o, oi) => (
                        <label key={oi} className="flex items-start gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer">
                          <input
                            type="radio"
                            name={`answer-${qi}`}
                            checked={playAnswers[qi] === oi}
                            onChange={() => {
                              const next = [...playAnswers];
                              next[qi] = oi;
                              setPlayAnswers(next);
                            }}
                            className="mt-1"
                          />
                          <span className="font-bold w-5">{String.fromCharCode(65 + oi)}.</span>
                          <span className="flex-1 text-sm">{o}</span>
                        </label>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <Card className={submitResult.passed ? 'bg-emerald-50 border-emerald-300' : 'bg-red-50 border-red-300'}>
                <CardContent className="py-6 text-center">
                  {submitResult.passed ? (
                    <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-2" />
                  ) : (
                    <XCircle className="w-16 h-16 text-red-500 mx-auto mb-2" />
                  )}
                  <h3 className="text-2xl font-bold">
                    {submitResult.passed ? 'TEST SUPERATO ✅' : 'TEST NON SUPERATO ❌'}
                  </h3>
                  <p className="text-4xl font-bold mt-3">{submitResult.score_pct}%</p>
                  <p className="text-gray-600 mt-1">
                    {submitResult.correct_count} / {submitResult.total_questions} risposte corrette
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    Punteggio minimo richiesto: {submitResult.passing_score_pct}%
                  </p>
                </CardContent>
              </Card>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPlayDialog(false)}>Chiudi</Button>
            {!submitResult && (
              <Button onClick={submitTest} disabled={submittingTest} data-testid="submit-test-btn">
                {submittingTest ? 'Invio...' : 'Invia risposte'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
